import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { ApiError } from '../services/api';
import { Wordmark } from '../shared/ui';
// Image générée (Figma AI, 07/10/2026) : installateur, pompe à chaleur, jardin ensoleillé.
import photoAccueil from '../assets/accueil-jardin.jpg';

// Page /login. Même composition que la connexion interne d'AdminV2 : à gauche
// l'identité KLY en bleu pétrole, à droite le formulaire seul sur fond papier.
const AdminLogin: React.FC = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Connexion impossible pour le moment. Réessayez dans quelques instants.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const champ = 'w-full pl-11 pr-11 py-3 bg-white border border-trait rounded-lg text-encre placeholder:text-graphite/70 focus:outline-none focus:border-sapin-600 focus:ring-2 focus:ring-sapin-600/20 transition-colors';

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <section className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-petrol-800 text-white p-12">
        <img src={photoAccueil} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover anim-kenburns" />
        <div className="absolute inset-0 bg-gradient-to-br from-petrol-900/85 via-petrol-800/60 to-petrol-700/30" />
        <div className="relative anim-fade-up"><Wordmark inverse /></div>
        <div className="relative max-w-md anim-fade-up anim-delay-2">
          <p className="text-3xl font-extrabold tracking-tight leading-tight">Le service après-vente des installateurs KLY.</p>
          <p className="mt-4 text-petrol-100/80 leading-relaxed">Dossiers, garanties constructeur, échanges avec les clients : tout ce qui arrive sur sav@ et assistance@ passe par ici.</p>
        </div>
        <p className="relative text-sm text-petrol-100/70 anim-fade-up anim-delay-3">KLY Groupe · Gennevilliers</p>
      </section>

      <section className="flex items-center justify-center p-6 sm:p-12 bg-papier">
        <div className="w-full max-w-sm anim-fade-up anim-delay-1">
          <div className="lg:hidden mb-10"><Wordmark /></div>
          <h1 className="text-2xl font-extrabold tracking-tight text-encre">Connexion</h1>
          <p className="mt-1 text-graphite">Espace réservé à l'équipe SAV.</p>

          {error && (
            <div role="alert" className="mt-6 p-3.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-800 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-encre mb-1.5">Adresse e-mail</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-graphite pointer-events-none" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="prenom@klygroupe.com"
                  autoComplete="username"
                  required
                  className={champ}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-encre mb-1.5">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-graphite pointer-events-none" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  autoComplete="current-password"
                  required
                  className={champ}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-graphite hover:text-encre">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="-mt-2 text-right">
              <Link to="/mot-de-passe-oublie" className="text-sm font-semibold text-sapin-700 hover:text-sapin-900">Mot de passe oublié ?</Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-sapin-700 hover:bg-sapin-800 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Connexion en cours</span>
                </>
              ) : (
                <span>Se connecter</span>
              )}
            </button>
          </form>

          <p className="mt-8 text-xs text-graphite">
            Accès réservé au personnel autorisé. Les connexions sont journalisées.
          </p>
        </div>
      </section>
    </div>
  );
};

export default AdminLogin;
